import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Users } from "lucide-react";
import { Link } from "react-router-dom";
import actionAsset from "@/assets/genre-action.png.asset.json";
import adventureAsset from "@/assets/genre-adventure.png.asset.json";
import casualAsset from "@/assets/genre-casual.png.asset.json";
import { useLanguage } from "@/contexts/LanguageContext";
import { translations } from "@/i18n/translations";

const Experiences = () => {
  const { language } = useLanguage();
  const t = translations[language].experiences;

  const experiences = [
    {
      title: t.action?.title || "Action",
      description: t.action?.description || "Experience intense action in our VR shooter and combat games. Perfect for adrenaline junkies.",
      image: actionAsset.url,
      genre: "Action",
      features: t.action?.features || ["Intense combat", "Co-op missions", "Adrenaline-packed"]
    },
    {
      title: t.adventure?.title || "Adventure",
      description: t.adventure?.description || "Embark on thrilling adventures with stunning worlds and epic quests.",
      image: adventureAsset.url,
      genre: "Adventure",
      features: t.adventure?.features || ["Epic storylines", "Beautiful worlds", "Exploration"]
    },
    {
      title: t.kidFriendly?.title || "Casual",
      description: t.kidFriendly?.description || "Fun and safe VR experiences perfect for the whole family. Colorful, engaging, and accessible for all ages.",
      image: casualAsset.url,
      genre: "Casual",
      features: t.kidFriendly?.features || ["Family fun", "Easy to play", "Colorful worlds"]
    }
  ];

  
  return (
    <section id="experiences" className="py-14 sm:py-24 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-8 sm:mb-16 animate-fade-in">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-4 px-4">
            {t.title} <span className="text-primary">{t.titleHighlight}</span>
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-3xl mx-auto px-4">
            {t.description}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-20">
          {experiences.map((experience, index) => (
              <Link 
                key={index} 
                to={`/experiences?genre=${encodeURIComponent(experience.genre)}`}
                className="block"
              >
                <Card
                  className="h-full bg-transparent border-border hover:border-primary/50 transition-colors duration-300 group animate-fade-in cursor-pointer overflow-hidden"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={experience.image}
                      alt={`${experience.title} - VR teambuilding game voor bedrijfsuitjes en groepsactiviteiten`}
                      className="w-full h-44 object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <CardHeader>
                    <CardTitle className="text-xl font-bold group-hover:text-primary transition-colors">
                      {experience.title}
                    </CardTitle>
                    <CardDescription className="text-muted-foreground">
                      {experience.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {experience.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center text-sm text-muted-foreground">
                          <div className="w-1.5 h-1.5 bg-primary rounded-full mr-2"></div>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </Link>
            ))}
        </div>


        {/* On-Location Service Highlight */}
        <div id="we-come-to-you" className="border border-border rounded-2xl animate-fade-in scroll-mt-24">
          <div className="p-8 sm:p-12">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-1 text-center md:text-left">
                <Users className="w-7 h-7 text-primary mb-5 mx-auto md:mx-0" />
                <h3 className="text-2xl sm:text-3xl font-bold tracking-tight mb-4">{t.weComeTo.title}</h3>
                <p className="text-muted-foreground text-base sm:text-lg mb-6 max-w-2xl">
                  {t.weComeTo.description}
                </p>
                <ul className="space-y-2 text-left">
                  {t.weComeTo.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center">
                      <div className="w-1.5 h-1.5 rounded-full mr-3 bg-primary"></div>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experiences;
